import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import BrandName from './BrandName';

const Login: React.FC = () => {
  const { signIn, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWorking(true);
    setMessage(null);
    try {
      await signIn({ username: email, password });
      navigate(from, { replace: true });
    } catch (error: any) {
      setMessage(error?.message || 'Login failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel — 60% */}
      <section
        className="relative flex flex-col lg:w-[60%] min-h-[38vh] lg:min-h-screen overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 42%, #6d28d9 100%)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 20% 20%, rgba(255,255,255,0.35) 0%, transparent 55%), radial-gradient(ellipse 50% 40% at 85% 75%, rgba(0,0,0,0.2) 0%, transparent 50%)',
          }}
        />
        <div className="relative z-10 flex flex-col flex-1 p-8 sm:p-10 lg:p-12">
          <img
            src={`${process.env.PUBLIC_URL}/hive-logo.png`}
            alt="HIVE"
            className="h-11 sm:h-14 w-auto max-w-[220px] object-contain object-left"
          />
          <div className="flex flex-1 flex-col justify-center max-w-xl mt-10 lg:mt-0 lg:py-16">
            <BrandName size="xl" className="mb-5" />
            <p className="text-xl sm:text-2xl text-white font-semibold leading-snug tracking-tight">
              Release AI Apps with Confidence
            </p>
            <ul className="mt-6 space-y-3.5 text-sm sm:text-base text-white/85 leading-relaxed max-w-lg list-none">
              {[
                'Test artifacts created once — validated across every release cycle',
                'Hallucination rates, bias, and RAG accuracy measured probabilistically',
                '1,000+ adversarial probes — jailbreaks, injections, DAN patterns',
                'Human expert review for policy compliance, not just factual accuracy',
                'OWASP LLM Top 10 mapped on every engagement',
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <footer className="relative z-10 shrink-0 px-8 sm:px-10 lg:px-12 py-4 text-center sm:text-left text-xs text-white/75">
          <span>© 2026 </span>
          <span className="font-semibold text-white">HIVE</span>
          <span className="mx-1.5 text-white/40" aria-hidden>
            ·
          </span>
          <a
            href="https://hivesprint.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-300 hover:text-amber-200 underline-offset-2 hover:underline"
          >
            hivesprint.ai
          </a>
        </footer>
      </section>

      {/* Login panel — 40% */}
      <section className="flex flex-1 flex-col lg:w-[40%] bg-slate-50 min-h-[50vh] lg:min-h-screen">
        <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <BrandName size="md" className="[&_span:first-child]:text-slate-900" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-8 sm:p-10 space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Sign in</h2>
              <p className="mt-1 text-sm text-slate-500">Use your organization account to continue</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
              {message && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-700">{message}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={working || loading}
                className="w-full inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                style={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                }}
              >
                {working || loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>
        </div>
        </div>
        <div className="shrink-0 px-4 sm:px-8 lg:px-12 py-4 min-h-[3.25rem]" aria-hidden />
      </section>
    </div>
  );
};

export default Login;
